import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { Markdown } from 'tiptap-markdown';
import { PaginationPlus } from 'tiptap-pagination-plus';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import PetitionService, {
  SectionApprovalStatus,
  PetitionDraft,
} from '@/api/petitions';
import {
  RiArrowGoBackLine as UndoIcon,
  RiArrowGoForwardLine as RedoIcon,
  RiH1 as H1Icon,
  RiH2 as H2Icon,
  RiH3 as H3Icon,
  RiBold as BoldIcon,
  RiItalic as ItalicIcon,
  RiStrikethrough as StrikeIcon,
  RiMarkPenLine as HighlightIcon,
  RiAlignLeft as AlignLeftIcon,
  RiAlignCenter as AlignCenterIcon,
  RiAlignRight as AlignRightIcon,
  RiAlignJustify as AlignJustifyIcon,
  RiListOrdered as OrderedListIcon,
  RiListUnordered as BulletListIcon,
  RiCheckFill as CheckIcon,
  RiFileCopyLine as CopyIcon,
  RiSearchEyeLine as CheckSourcesIcon,
  RiMagicLine as RegenerateIcon,
  RiArrowLeftSLine as PrevIcon,
  RiArrowRightSLine as NextIcon,
  RiCheckboxCircleLine as ApproveIcon,
  RiCloseCircleLine as RejectIcon,
  RiTimeLine as PendingIcon,
  RiArrowUpSLine as MoveUpIcon,
  RiArrowDownSLine as MoveDownIcon,
  RiAddLine as AddIcon,
  RiEditLine as EditIcon,
  RiDeleteBinLine as DeleteIcon,
  RiDraggable as DragIcon,
  RiCloseLine as CloseIcon,
} from 'react-icons/ri';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import './petitionEditor.css';

export interface PetitionEditorProps {
  petitionId: string;
  caseId?: string;
  initialContent?: Record<string, string>;
  onSelectionTextChange?: (text: string) => void;
  onCheckSources?: (text: string) => void;
}

// BUGFIX: Helper function to check if visa type is O-1A
// Commercial success is NOT an O-1A criterion per 8 CFR 214.2(o)(3)(iii)
// O-1A has only 8 criteria (A-H). Commercial success is an EB-1A criterion (x).
const isO1AVisaType = (visaType: string | undefined): boolean => {
  if (!visaType) return false;
  const normalized = visaType.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return normalized === 'O1A' || normalized === 'O1';
};

// Helper function to filter sections based on visa type
// Uses generic type to preserve the exact shape of the input array
const filterSectionsForVisaType = <T extends { key: string }>(
  sections: T[],
  visaType: string | undefined
): T[] => {
  if (isO1AVisaType(visaType)) {
    // Remove commercial_success_section for O-1A visa type
    return sections.filter(s => s.key !== 'commercial_success_section');
  }
  return sections;
};

// Define section structure with labels and keys - MUST match backend section names in petition-stages-optimized.ts
const PETITION_SECTIONS = [
  { key: 'cover_letter', label: 'Cover Letter', group: 'Introduction' },
  {
    key: 'introduction',
    label: 'Statutory Introduction',
    group: 'Introduction',
  },
  {
    key: 'background_summary',
    label: 'Background Summary',
    group: 'Introduction',
  },
  { key: 'legal_framework', label: 'Legal Framework', group: 'Legal' },
  { key: 'field_of_expertise', label: 'Field of Expertise', group: 'Legal' },
  { key: 'table_of_contents', label: 'Table of Contents', group: 'Structure' },
  { key: 'exhibit_list', label: 'List of Exhibits', group: 'Structure' },
  {
    key: 'awards_section',
    label: 'Awards & Recognition',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'membership_section',
    label: 'Membership in Associations',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'media_section',
    label: 'Media Coverage',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'judging_section',
    label: 'Judging Work of Others',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'contributions_section',
    label: 'Original Contributions',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'authorship_section',
    label: 'Scholarly Articles',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'critical_capacity_section',
    label: 'Critical or Leading Role',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'high_remuneration_section',
    label: 'High Remuneration',
    group: 'Section 1 - Evidence',
  },
  {
    key: 'commercial_success_section',
    label: 'Commercial Success',
    group: 'Section 1 - Evidence',
  },
  { key: 'conclusion_section', label: 'Conclusion', group: 'Conclusion' },
] as const;

const PetitionEditor: React.FC<PetitionEditorProps> = ({
  petitionId,
  caseId,
  initialContent = {},
  onSelectionTextChange,
  onCheckSources,
}) => {
  // Section navigation state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [availableSections, setAvailableSections] = useState<
    Array<{ key: string; label: string; group: string }>
  >([]);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  // Petition draft state
  const [petitionDraft, setPetitionDraft] = useState<PetitionDraft | null>(
    null
  );
  const [loadingDraft, setLoadingDraft] = useState(true);

  // Auto-save state
  const [lastSavedContent, setLastSavedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // Approval state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  // Regeneration state
  const [showRegen, setShowRegen] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [showFullRegen, setShowFullRegen] = useState(false);
  const [fullRegenPrompt, setFullRegenPrompt] = useState('');
  const [fullRegenLoading, setFullRegenLoading] = useState(false);
  const [fullRegenError, setFullRegenError] = useState<string | null>(null);

  // Copy state
  const [copied, setCopied] = useState(false);

  // Section management state
  const [isCreating, setIsCreating] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [editingSectionKey, setEditingSectionKey] = useState<string | null>(
    null
  );
  const [editingTitle, setEditingTitle] = useState('');
  const [deletingSectionKey, setDeletingSectionKey] = useState<string | null>(
    null
  );

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper function to build availableSections from draft
  const buildSectionsFromDraft = useCallback((draft: PetitionDraft) => {
    console.log('buildSectionsFromDraft: Input draft', {
      tableOfContents: draft.tableOfContents,
      tocKeys: Object.keys(draft.tableOfContents || {}),
      sectionsKeys: Object.keys(draft.sections || {}),
      petitionType: draft.petitionType,
    });

    const toc = draft.tableOfContents || {};
    let sections = Object.keys(toc)
      .map(key => ({
        key,
        label: toc[key].title,
        order: toc[key].order,
        group: '', // We don't have group info in draft, use empty string
      }))
      .sort((a, b) => a.order - b.order);

    // BUGFIX: Filter out commercial_success_section for O-1A visa type
    sections = filterSectionsForVisaType(sections, draft.petitionType);

    console.log('buildSectionsFromDraft: Built sections', sections);

    // Fallback to PETITION_SECTIONS if no sections in TOC
    if (sections.length === 0) {
      console.log(
        'buildSectionsFromDraft: No sections in TOC, using PETITION_SECTIONS fallback'
      );
      // BUGFIX: Also filter fallback sections based on visa type
      return filterSectionsForVisaType([...PETITION_SECTIONS], draft.petitionType);
    }

    return sections;
  }, []);

  // Load petition draft
  useEffect(() => {
    const loadDraft = async () => {
      // Guard: Don't load if petitionId is undefined
      if (!petitionId) {
        console.warn(
          'PetitionEditor: petitionId is undefined, skipping draft load'
        );
        setLoadingDraft(false);
        return;
      }

      try {
        setLoadingDraft(true);
        const response = await PetitionService.getPetitionDraft(petitionId);
        if (response.success && response.data) {
          setPetitionDraft(response.data);

          // Build sections list from draft's table of contents
          const sections = buildSectionsFromDraft(response.data);
          setAvailableSections(sections);

          // Set initial content for first section
          if (sections.length > 0) {
            const firstSectionData = response.data.sections[sections[0].key];
            setLastSavedContent(firstSectionData?.content || '');
          }
        } else {
          // Fallback to initialContent if drafting API fails - show all sections
          const sections = [...PETITION_SECTIONS];
          setAvailableSections(sections);

          if (sections.length > 0) {
            setLastSavedContent(initialContent[sections[0].key] || '');
          }
        }
      } catch (error) {
        console.error('Failed to load petition draft:', error);
        // Fallback to initialContent - show all sections
        const sections = [...PETITION_SECTIONS];
        setAvailableSections(sections);

        if (sections.length > 0) {
          setLastSavedContent(initialContent[sections[0].key] || '');
        }
      } finally {
        setLoadingDraft(false);
      }
    };

    loadDraft();
  }, [petitionId, initialContent, buildSectionsFromDraft]);

  // Poll for updates when petition is generating or has no content
  useEffect(() => {
    if (!petitionId || !petitionDraft) return;

    const isGenerating =
      petitionDraft.status === 'in_progress' ||
      petitionDraft.status === 'generating';

    const hasNoContent = availableSections.length === 0;
    const isFailed = petitionDraft.status === 'failed';

    // Check if petition is already complete - if so, force reload immediately
    const isAlreadyComplete =
      petitionDraft.status === 'completed' ||
      petitionDraft.status === 'generated' ||
      petitionDraft.status === 'done';

    if (isAlreadyComplete && hasNoContent) {
      console.log(
        'Petition is already complete but has no content - forcing reload...'
      );
      window.location.reload();
      return;
    }

    // Continue polling if generating OR has no content (unless failed or already complete)
    if (!isGenerating && !hasNoContent) return;
    if (isFailed) return;
    if (isAlreadyComplete && !hasNoContent) return; // Already complete with content, no need to poll

    console.log(
      'Petition is still generating or empty, polling for updates...'
    );
    const pollInterval = setInterval(async () => {
      try {
        const response = await PetitionService.getPetitionDraft(petitionId);
        if (response.success && response.data) {
          const updatedStatus = response.data.status;
          console.log('Poll update - petition status:', updatedStatus);

          setPetitionDraft(response.data);

          // Rebuild sections if needed
          const sections = buildSectionsFromDraft(response.data);
          setAvailableSections(sections);

          // Stop polling when we have content and status is terminal
          const hasContent = sections.length > 0;
          const isTerminal =
            updatedStatus === 'completed' ||
            updatedStatus === 'generated' ||
            updatedStatus === 'done' ||
            updatedStatus === 'failed';

          if (hasContent && isTerminal) {
            console.log(
              'Petition generation completed with content, stopping poll'
            );
            clearInterval(pollInterval);
            // Force page reload to ensure fresh data is displayed
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Failed to poll petition status:', error);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [
    petitionId,
    petitionDraft?.status,
    availableSections.length,
    buildSectionsFromDraft,
  ]);

  const currentSection = availableSections[currentSectionIndex];
  const currentSectionData =
    currentSection && petitionDraft?.sections[currentSection.key];
  const currentSectionContent =
    currentSectionData?.content ||
    (currentSection ? initialContent[currentSection.key] || '' : '');
  const currentApprovalStatus =
    currentSectionData?.approvalStatus || SectionApprovalStatus.DRAFT;

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left',
      }),
      Highlight,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      PaginationPlus.configure({
        pageHeight: 940,
        pageGap: 48,
        pageBreakBackground: '#f6f9fc',
        footerRight: '',
        footerLeft: 'Page {page}',
        headerLeft: '',
        headerRight: '',
      }),
      Markdown,
    ],
    content: currentSectionContent || '',
    onSelectionUpdate: ({ editor }) => {
      const state = editor.state;
      const { from, to } = state.selection;
      const selected = editor.state.doc.textBetween(from, to, ' ');
      onSelectionTextChange?.(selected);
    },
    editorProps: {
      attributes: {
        class: 'editor-content w-full focus:outline-none outline-none',
      },
    },
  });

  // Update editor content when section changes
  useEffect(() => {
    if (editor && currentSection) {
      const content =
        petitionDraft?.sections[currentSection.key]?.content ||
        initialContent[currentSection.key] ||
        '';
      const storage = editor.storage as any;
      const currentMarkdown =
        storage.markdown?.getMarkdown?.() || editor.getText();
      if (currentMarkdown !== content) {
        editor.commands.setContent(content);
        setLastSavedContent(content);
      }
    }
  }, [currentSectionIndex, currentSection, editor, petitionDraft]);

  // Scroll editor container to top when section changes
  useEffect(() => {
    if (editorContainerRef.current) {
      editorContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentSectionIndex]);

  const isActive = useCallback(
    (name: string, attrs: Record<string, any> = {}) =>
      !!editor?.isActive(name as any, attrs),
    [editor]
  );

  const run = useCallback(
    (fn: (e: typeof editor) => void) => () => {
      if (!editor) return;
      fn(editor);
    },
    [editor]
  );

  // Auto-save function using drafting API
  const saveContent = useCallback(
    async (content: string) => {
      if (!petitionId || !currentSection || !content) return;

      try {
        setIsSaving(true);
        setSaveError(null);

        const response = await PetitionService.updateDraftSection(
          petitionId,
          currentSection.key,
          content,
          'current-user' // TODO: Get from auth context
        );

        if (response.success && response.data) {
          setPetitionDraft(response.data);
        }

        setLastSavedContent(content);
        setLastSaveTime(new Date());
      } catch (error: any) {
        console.error('Failed to save petition section:', error);
        setSaveError(error.message || 'Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    },
    [petitionId, currentSection]
  );

  // Auto-save effect - debounced
  useEffect(() => {
    if (!editor || !currentSection) return;

    const saveTimer = setTimeout(() => {
      // Get content as markdown to preserve formatting
      const storage = editor.storage as any;
      const currentContent =
        storage.markdown?.getMarkdown?.() || editor.getText();

      // Only save if content has changed
      if (currentContent && currentContent !== lastSavedContent) {
        saveContent(currentContent);
      }
    }, 3000); // Save after 3 seconds of no changes

    return () => clearTimeout(saveTimer);
  }, [
    editor,
    editor?.state.doc,
    lastSavedContent,
    saveContent,
    currentSection,
  ]);

  const copyText = useCallback(async () => {
    if (!editor) return;
    try {
      await navigator.clipboard.writeText(editor.getText());
      setCopied(true);
      analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_COPIED, {
        petition_id: petitionId,
        section_key: currentSection?.key,
        section_name: currentSection?.label,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  }, [editor]);

  // Approve section handler
  const handleApproveSection = useCallback(async () => {
    console.log('🔵 handleApproveSection called', {
      petitionId,
      currentSection: currentSection
        ? {
            key: currentSection.key,
            label: currentSection.label,
          }
        : null,
    });

    if (!petitionId || !currentSection) {
      console.warn('⚠️ Early return - missing petitionId or currentSection', {
        petitionId,
        currentSection,
      });
      return;
    }

    try {
      setApprovalLoading(true);
      setApprovalError(null);

      console.log('📤 Calling PetitionService.approveSection with:', {
        petitionId,
        sectionKey: currentSection.key,
        userId: 'current-user',
      });

      const response = await PetitionService.approveSection(
        petitionId,
        currentSection.key,
        'current-user', // TODO: Get from auth context
        undefined
      );

      console.log('📥 PetitionService.approveSection response:', {
        success: response.success,
        hasData: !!response.data,
        response,
      });

      if (response.success && response.data) {
        console.log('✅ Approval successful, updating petition draft');
        setPetitionDraft(response.data);
        analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_APPROVED, {
          petition_id: petitionId,
          section_key: currentSection.key,
          section_name: currentSection.label,
        });
        setShowApproveModal(false);
      } else {
        console.warn('⚠️ Response missing success or data', response);
        setApprovalError('Unexpected response from server');
      }
    } catch (error: any) {
      console.error('❌ Failed to approve section:', {
        error,
        message: error.message,
        response: error.response,
        stack: error.stack,
      });
      setApprovalError(error.message || 'Failed to approve section');
    } finally {
      setApprovalLoading(false);
      console.log('🔵 handleApproveSection completed');
    }
  }, [petitionId, currentSection]);

  // Reject section handler
  const handleRejectSection = useCallback(async () => {
    if (!petitionId || !currentSection || !rejectReason.trim()) {
      setApprovalError('Please provide a reason for rejection');
      return;
    }

    try {
      setApprovalLoading(true);
      setApprovalError(null);

      const response = await PetitionService.rejectSection(
        petitionId,
        currentSection.key,
        'current-user', // TODO: Get from auth context
        rejectReason,
        undefined
      );

      if (response.success && response.data) {
        setPetitionDraft(response.data);
        analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_DISAPPROVED, {
          petition_id: petitionId,
          section_key: currentSection.key,
          section_name: currentSection.label,
          reason_length: rejectReason.length,
        });
        setShowRejectModal(false);
        setRejectReason('');
      }
    } catch (error: any) {
      console.error('Failed to reject section:', error);
      setApprovalError(error.message || 'Failed to reject section');
    } finally {
      setApprovalLoading(false);
    }
  }, [petitionId, currentSection, rejectReason]);

  const navigateSection = useCallback(
    (direction: 'prev' | 'next') => {
      setCurrentSectionIndex(prev => {
        if (direction === 'prev') {
          return prev > 0 ? prev - 1 : prev;
        } else {
          return prev < availableSections.length - 1 ? prev + 1 : prev;
        }
      });
    },
    [availableSections.length]
  );

  // Move section up or down in the list
  const moveSectionPosition = useCallback(
    (fromIndex: number, direction: 'up' | 'down') => {
      const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;

      // Check bounds
      if (toIndex < 0 || toIndex >= availableSections.length) return;

      // Create new array with swapped positions
      const newSections = [...availableSections];
      [newSections[fromIndex], newSections[toIndex]] = [
        newSections[toIndex],
        newSections[fromIndex],
      ];

      setAvailableSections(newSections);

      // Update current section index if needed
      if (currentSectionIndex === fromIndex) {
        setCurrentSectionIndex(toIndex);
      } else if (currentSectionIndex === toIndex) {
        setCurrentSectionIndex(fromIndex);
      }
    },
    [availableSections, currentSectionIndex]
  );

  // Section management handlers
  const handleCreateSection = useCallback(
    async (title: string) => {
      if (!petitionId || !title.trim()) {
        console.log('handleCreateSection: Missing petitionId or title', {
          petitionId,
          title,
        });
        return;
      }

      try {
        console.log('handleCreateSection: Creating section', {
          petitionId,
          title: title.trim(),
        });
        const createResponse = await PetitionService.createSection(
          petitionId,
          title.trim(),
          '',
          'current-user-id'
        );
        console.log('handleCreateSection: Create response', createResponse);

        // Reload draft to get updated sections
        const updatedDraft = await PetitionService.getPetitionDraft(petitionId);
        console.log('handleCreateSection: Updated draft', {
          success: updatedDraft.success,
          sectionsCount: updatedDraft.data
            ? Object.keys(updatedDraft.data.sections || {}).length
            : 0,
          tocCount: updatedDraft.data
            ? Object.keys(updatedDraft.data.tableOfContents || {}).length
            : 0,
          sectionKeys: updatedDraft.data
            ? Object.keys(updatedDraft.data.sections || {})
            : [],
        });

        if (updatedDraft.success && updatedDraft.data) {
          setPetitionDraft(updatedDraft.data);
          const sections = buildSectionsFromDraft(updatedDraft.data);
          console.log(
            'handleCreateSection: Built sections from draft',
            sections
          );
          setAvailableSections(sections);

          analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_ADDED, {
            petition_id: petitionId,
            section_name: title.trim(),
          });
        }

        setIsCreating(false);
        setNewSectionTitle('');
      } catch (error: any) {
        console.error('Failed to create section:', error);
        alert(error.message || 'Failed to create section');
      }
    },
    [petitionId, buildSectionsFromDraft]
  );

  const handleDeleteSection = useCallback(
    async (sectionKey: string) => {
      if (!petitionId || !sectionKey) return;

      try {
        await PetitionService.deleteSection(
          petitionId,
          sectionKey,
          'current-user-id'
        );

        // Reload draft to get updated sections
        const updatedDraft = await PetitionService.getPetitionDraft(petitionId);
        if (updatedDraft.success && updatedDraft.data) {
          setPetitionDraft(updatedDraft.data);

          // Update available sections list from draft
          const updatedSections = buildSectionsFromDraft(updatedDraft.data);
          setAvailableSections(updatedSections);

          analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_DELETED, {
            petition_id: petitionId,
            section_key: sectionKey,
          });

          // Adjust current section index if needed
          if (currentSectionIndex >= updatedSections.length) {
            setCurrentSectionIndex(Math.max(0, updatedSections.length - 1));
          }
        }

        setDeletingSectionKey(null);
      } catch (error: any) {
        console.error('Failed to delete section:', error);
        alert(error.message || 'Failed to delete section');
      }
    },
    [petitionId, currentSectionIndex, buildSectionsFromDraft]
  );

  const handleRenameSection = useCallback(
    async (sectionKey: string, newTitle: string) => {
      if (!petitionId || !sectionKey || !newTitle.trim()) return;

      try {
        await PetitionService.renameSection(
          petitionId,
          sectionKey,
          newTitle.trim(),
          'current-user-id'
        );

        // Reload draft to get updated sections
        const updatedDraft = await PetitionService.getPetitionDraft(petitionId);
        if (updatedDraft.success && updatedDraft.data) {
          setPetitionDraft(updatedDraft.data);

          // Update available sections list from draft
          const updatedSections = buildSectionsFromDraft(updatedDraft.data);
          setAvailableSections(updatedSections);

          analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_RENAMED, {
            petition_id: petitionId,
            section_key: sectionKey,
            new_name: newTitle.trim(),
          });
        }

        setEditingSectionKey(null);
        setEditingTitle('');
      } catch (error: any) {
        console.error('Failed to rename section:', error);
        alert(error.message || 'Failed to rename section');
      }
    },
    [petitionId, buildSectionsFromDraft]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = availableSections.findIndex(s => s.key === active.id);
      const newIndex = availableSections.findIndex(s => s.key === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const newSections = arrayMove(availableSections, oldIndex, newIndex);
      setAvailableSections(newSections);

      // Update current section index if the active section was moved
      if (currentSectionIndex === oldIndex) {
        setCurrentSectionIndex(newIndex);
      } else if (currentSectionIndex === newIndex) {
        setCurrentSectionIndex(
          oldIndex > newIndex
            ? currentSectionIndex + 1
            : currentSectionIndex - 1
        );
      }

      try {
        analytics.track(ANALYTICS_EVENTS.PETITION_SECTION_MOVED, {
          case_id: caseId,
          petition_id: petitionId,
          section_key: active.id as string,
          old_index: oldIndex,
          new_index: newIndex,
        });

        const newOrder = newSections.map(s => s.key);
        await PetitionService.reorderSections(
          petitionId,
          newOrder,
          'current-user-id'
        );

        // Reload draft to confirm changes
        const updatedDraft = await PetitionService.getPetitionDraft(petitionId);
        if (updatedDraft.success && updatedDraft.data) {
          setPetitionDraft(updatedDraft.data);
        }
      } catch (error: any) {
        console.error('Failed to reorder sections:', error);
        // Revert on error - reload draft to restore original order
        const updatedDraft = await PetitionService.getPetitionDraft(petitionId);
        if (updatedDraft.success && updatedDraft.data) {
          setPetitionDraft(updatedDraft.data);
          const sections = buildSectionsFromDraft(updatedDraft.data);
          setAvailableSections(sections);
        }
        alert(error.message || 'Failed to reorder sections');
      }
    },
    [petitionId, availableSections, currentSectionIndex, buildSectionsFromDraft]
  );

  // UI helper functions
  const handleStartCreate = useCallback(() => {
    setIsCreating(true);
    setNewSectionTitle('');
  }, []);

  const handleCancelCreate = useCallback(() => {
    setIsCreating(false);
    setNewSectionTitle('');
  }, []);

  const handleStartEdit = useCallback(
    (sectionKey: string) => {
      const section = availableSections.find(s => s.key === sectionKey);
      if (section) {
        setEditingSectionKey(sectionKey);
        setEditingTitle(section.label);
      }
    },
    [availableSections]
  );

  const handleSaveEdit = useCallback(() => {
    if (editingSectionKey && editingTitle.trim()) {
      handleRenameSection(editingSectionKey, editingTitle);
    }
  }, [editingSectionKey, editingTitle, handleRenameSection]);

  const handleCancelEdit = useCallback(() => {
    setEditingSectionKey(null);
    setEditingTitle('');
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deletingSectionKey) {
      handleDeleteSection(deletingSectionKey);
    }
  }, [deletingSectionKey, handleDeleteSection]);

  const toolbarButton = useCallback(
    ({
      onClick,
      active,
      label,
      icon,
      disabled,
    }: {
      onClick: () => void;
      active?: boolean;
      label: string;
      icon: React.ReactNode;
      disabled?: boolean;
    }) => (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={clsx(
          'flex items-center justify-center rounded px-2 py-1 text-neutral-700 hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
          active && 'bg-neutral-200 text-neutral-800 border border-neutral-300'
        )}
        aria-label={label}
        title={label}
      >
        {icon}
      </button>
    ),
    []
  );

  // Helper function to get approval status color and label
  const getApprovalStatusDisplay = useCallback(
    (status: SectionApprovalStatus) => {
      switch (status) {
        case SectionApprovalStatus.APPROVED:
          return {
            label: 'Approved',
            color: 'text-green-700 bg-green-50 border-green-200',
            icon: <ApproveIcon className="h-4 w-4" />,
          };
        case SectionApprovalStatus.REJECTED:
          return {
            label: 'Rejected',
            color: 'text-red-700 bg-red-50 border-red-200',
            icon: <RejectIcon className="h-4 w-4" />,
          };
        case SectionApprovalStatus.NEEDS_APPROVAL:
          return {
            label: 'Needs Approval',
            color: 'text-amber-700 bg-amber-50 border-amber-200',
            icon: <PendingIcon className="h-4 w-4" />,
          };
        default:
          return {
            label: 'Draft',
            color: 'text-neutral-600 bg-neutral-50 border-neutral-200',
            icon: <PendingIcon className="h-4 w-4" />,
          };
      }
    },
    []
  );

  // Sortable section item component
  const SortableSectionItem = ({
    section,
    index,
    isActive,
  }: {
    section: { key: string; label: string; group: string };
    index: number;
    isActive: boolean;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: section.key });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const sectionData = petitionDraft?.sections[section.key];
    const approvalStatus =
      sectionData?.approvalStatus || SectionApprovalStatus.DRAFT;
    const statusDisplay = getApprovalStatusDisplay(approvalStatus);
    const isEditing = editingSectionKey === section.key;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={clsx(
          'group relative border-b border-neutral-200',
          isActive && 'bg-primary-50 border-primary-200'
        )}
      >
        <div className="flex items-start">
          {/* Drag Handle */}
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex-shrink-0 p-3 cursor-grab active:cursor-grabbing hover:bg-neutral-100 transition-colors"
            aria-label="Drag to reorder"
          >
            <DragIcon className="h-4 w-4 text-neutral-400" />
          </button>

          {/* Section Content */}
          <button
            type="button"
            onClick={() => setCurrentSectionIndex(index)}
            className={clsx(
              'flex-1 min-w-0 text-left px-4 py-3 pr-3 transition-colors overflow-hidden',
              isActive ? 'bg-primary-50' : 'hover:bg-neutral-100'
            )}
          >
            <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1 truncate">
              {section.group}
            </div>

            {isEditing ? (
              <div
                className="flex items-center gap-2 mt-1"
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={editingTitle}
                  onChange={e => setEditingTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSaveEdit();
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                  className="flex-1 min-w-0 px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="p-1 rounded hover:bg-green-100 text-green-600 flex-shrink-0"
                  disabled={!editingTitle.trim()}
                  title="Save"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="p-1 rounded hover:bg-red-100 text-red-600 flex-shrink-0"
                  title="Cancel"
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div
                className="text-sm font-medium text-neutral-900 truncate"
                title={section.label}
              >
                {section.label}
              </div>
            )}

            {/* Approval Status Badge */}
            <div className="mt-1.5">
              <span
                className={clsx(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border',
                  statusDisplay.color
                )}
              >
                {statusDisplay.icon}
                {statusDisplay.label}
              </span>
            </div>
          </button>
        </div>

        {/* Action Buttons - Absolutely positioned, hidden when editing */}
        {!isEditing && (
          <div
            className={clsx(
              'absolute right-0 top-0 bottom-0 flex items-center gap-1 pr-2 pl-6 opacity-0 group-hover:opacity-100 transition-opacity',
              isActive
                ? 'bg-gradient-to-l from-primary-50 via-primary-50 to-transparent'
                : 'bg-gradient-to-l from-neutral-50 via-neutral-50 to-transparent group-hover:from-neutral-100 group-hover:via-neutral-100'
            )}
          >
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleStartEdit(section.key);
              }}
              className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition-colors"
              aria-label="Rename section"
              title="Rename section"
            >
              <EditIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                setDeletingSectionKey(section.key);
              }}
              className="p-1.5 rounded hover:bg-red-100 text-red-600 transition-colors"
              aria-label="Delete section"
              title="Delete section"
            >
              <DeleteIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Show loading state while fetching draft
  if (loadingDraft) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-neutral-700 font-medium">Loading petition...</p>
        </div>
      </div>
    );
  }

  // Check if petition is currently being generated
  const isGenerating =
    petitionDraft?.status === 'in_progress' ||
    petitionDraft?.status === 'generating';

  // If petition has no sections, treat it as still generating (unless explicitly failed)
  const hasNoContent = availableSections.length === 0;
  const isFailed = petitionDraft?.status === 'failed';

  // Show generating state if petition is being created OR still loading draft
  // Use loadingDraft to avoid showing loader after data has loaded but before sections populated
  if ((isGenerating || (loadingDraft && hasNoContent)) && !isFailed) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="animate-spin h-12 w-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-neutral-900 font-semibold text-lg mb-2">
            Generating petition...
          </p>
          <p className="text-neutral-600 text-sm">
            Your petition is being generated with AI. This may take a few
            minutes. The page will update automatically when ready.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-neutral-500">
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
            <span>Processing documents and generating sections</span>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state only if petition failed or is in a terminal state with no content
  if (hasNoContent) {
    return (
      <div className="w-full p-8 text-center">
        <p className="text-neutral-500">
          {isFailed
            ? 'Petition generation failed. Please try again.'
            : 'No petition content available to edit.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left Sidebar - Section Navigation */}
      <div className="w-80 border-r border-neutral-200 bg-neutral-50 flex flex-col h-screen overflow-hidden">
        {/* Header with Add Button */}
        <div className="p-4 border-b border-neutral-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">
                Petition Sections
              </h3>
              <p className="text-xs text-neutral-500 mt-1">
                {availableSections.length} sections total
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartCreate}
              className="p-2 rounded-md hover:bg-primary-100 text-primary-600 transition-colors"
              title="Add new section"
              aria-label="Add new section"
            >
              <AddIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Create Section Form */}
        {isCreating && (
          <div className="p-4 border-b border-neutral-200 bg-white">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSectionTitle}
                onChange={e => setNewSectionTitle(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newSectionTitle.trim()) {
                    handleCreateSection(newSectionTitle);
                  } else if (e.key === 'Escape') {
                    handleCancelCreate();
                  }
                }}
                placeholder="Enter section title..."
                className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
              <button
                type="button"
                onClick={() => handleCreateSection(newSectionTitle)}
                disabled={!newSectionTitle.trim()}
                className="p-2 rounded-md hover:bg-green-100 text-green-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Save"
                aria-label="Save new section"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleCancelCreate}
                className="p-2 rounded-md hover:bg-red-100 text-red-600 transition-colors"
                title="Cancel"
                aria-label="Cancel"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Sections List with Drag and Drop */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={availableSections.map(s => s.key)}
              strategy={verticalListSortingStrategy}
            >
              {availableSections.map((section, index) => (
                <SortableSectionItem
                  key={section.key}
                  section={section}
                  index={index}
                  isActive={index === currentSectionIndex}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* Right Side - Editor Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="sticky top-0 z-50 w-full bg-neutral-50 border-b border-neutral-200 shadow-sm py-2 px-2 flex flex-wrap items-center justify-between gap-2">
          <div className="toolbar flex flex-wrap gap-1 items-center">
            {toolbarButton({
              onClick: run(e => e.chain().focus().undo().run()),
              label: 'Undo',
              icon: <UndoIcon />,
            })}
            {toolbarButton({
              onClick: run(e => e.chain().focus().redo().run()),
              label: 'Redo',
              icon: <RedoIcon />,
            })}

            <div className="block w-px h-5 bg-neutral-300 mx-2" />

            {toolbarButton({
              onClick: run(e =>
                e.chain().focus().toggleHeading({ level: 1 }).run()
              ),
              label: 'H1',
              icon: <H1Icon />,
              active: isActive('heading', { level: 1 }),
            })}
            {toolbarButton({
              onClick: run(e =>
                e.chain().focus().toggleHeading({ level: 2 }).run()
              ),
              label: 'H2',
              icon: <H2Icon />,
              active: isActive('heading', { level: 2 }),
            })}
            {toolbarButton({
              onClick: run(e =>
                e.chain().focus().toggleHeading({ level: 3 }).run()
              ),
              label: 'H3',
              icon: <H3Icon />,
              active: isActive('heading', { level: 3 }),
            })}
            {toolbarButton({
              onClick: run(e => e.chain().focus().setTextAlign('center').run()),
              label: 'Align center',
              icon: <AlignCenterIcon />,
              active: isActive({ textAlign: 'center' } as any),
            })}
            {toolbarButton({
              onClick: run(e => e.chain().focus().setTextAlign('right').run()),
              label: 'Align right',
              icon: <AlignRightIcon />,
              active: isActive({ textAlign: 'right' } as any),
            })}
            {toolbarButton({
              onClick: run(e =>
                e.chain().focus().setTextAlign('justify').run()
              ),
              label: 'Align justify',
              icon: <AlignJustifyIcon />,
              active: isActive({ textAlign: 'justify' } as any),
            })}

            <div className="block w-px h-5 bg-neutral-300 mx-2" />

            {toolbarButton({
              onClick: run(e => e.chain().focus().toggleBulletList().run()),
              label: 'Bullet list',
              icon: <BulletListIcon />,
              active: isActive('bulletList'),
            })}
            {toolbarButton({
              onClick: run(e => e.chain().focus().toggleOrderedList().run()),
              label: 'Ordered list',
              icon: <OrderedListIcon />,
              active: isActive('orderedList'),
            })}

            <div className="block w-px h-5 bg-neutral-300 mx-2" />

            {/* Regenerate Section Button */}
            <button
              type="button"
              onClick={() => setShowFullRegen(true)}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 active:bg-blue-200 transition-colors shadow-sm"
              aria-label="Regenerate Section"
              title="Regenerate Section"
            >
              <RegenerateIcon />
              <span className="text-sm font-medium">Regenerate Section</span>
            </button>
          </div>

          {/* Save Status Indicator */}
          <div className="flex items-center gap-2 text-sm text-neutral-600 px-2">
            {isSaving && (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            )}
            {!isSaving && lastSaveTime && (
              <span className="text-neutral-500">
                Saved {new Date(lastSaveTime).toLocaleTimeString()}
              </span>
            )}
            {saveError && (
              <span className="text-red-600 text-xs">{saveError}</span>
            )}
          </div>
        </div>

        {/* Section Header with Approve/Reject */}
        <div className="sticky top-[57px] z-40 w-full bg-white border-b border-neutral-200 shadow-sm">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigateSection('prev')}
                  disabled={currentSectionIndex === 0}
                  className="p-2 rounded-md hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous section"
                >
                  <PrevIcon className="h-5 w-5" />
                </button>

                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-500 uppercase tracking-wide">
                    {currentSection?.group}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-neutral-900">
                      {currentSection?.label}
                    </span>
                    {/* Approval Status Badge */}
                    {(() => {
                      const statusDisplay = getApprovalStatusDisplay(
                        currentApprovalStatus
                      );
                      return (
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border',
                            statusDisplay.color
                          )}
                        >
                          {statusDisplay.icon}
                          {statusDisplay.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigateSection('next')}
                  disabled={
                    currentSectionIndex === availableSections.length - 1
                  }
                  className="p-2 rounded-md hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next section"
                >
                  <NextIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex items-center gap-3">
                {/* Approve/Reject Buttons */}
                {currentApprovalStatus !== SectionApprovalStatus.APPROVED && (
                  <button
                    type="button"
                    onClick={() => setShowApproveModal(true)}
                    disabled={loadingDraft}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ApproveIcon className="h-4 w-4" />
                    Approve
                  </button>
                )}
                {currentApprovalStatus !== SectionApprovalStatus.REJECTED && (
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(true)}
                    disabled={loadingDraft}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RejectIcon className="h-4 w-4" />
                    Reject
                  </button>
                )}
                <div className="text-xs text-neutral-500">
                  Section {currentSectionIndex + 1} of{' '}
                  {availableSections.length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div ref={editorContainerRef} className="flex-1 overflow-y-auto">
          <div className="max-w-4xl w-full mx-auto relative pb-6">
            <div className="w-full min-h-screen px-2 flex flex-col items-center">
              <EditorContent editor={editor} className="editor-content" />
              {editor && (
                <BubbleMenu
                  editor={editor}
                  appendTo={() => document.body}
                  options={{ strategy: 'fixed' }}
                  className="rounded-md bg-white border shadow-md px-2.5 py-1.5 text-sm flex items-center gap-1.5 z-[9999]"
                  style={{ zIndex: 9999 }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      const { from, to } = editor.state.selection;
                      const text = editor.state.doc
                        .textBetween(from, to, ' ')
                        .trim();
                      if (text) {
                        onCheckSources?.(text);
                      }
                    }}
                    className="flex items-center justify-center rounded-md px-3 py-1.5 text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 active:bg-blue-200 transition-colors shadow-sm"
                    aria-label="Check sources"
                    title="Check sources in case documents"
                  >
                    <CheckSourcesIcon />
                    <span className="ml-1">Check sources</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const { from, to } = editor.state.selection;
                      const text = editor.state.doc
                        .textBetween(from, to, ' ')
                        .trim();
                      if (text) {
                        // Open web search for CFR references or selected text
                        const searchQuery = encodeURIComponent(text);
                        window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
                      }
                    }}
                    className="flex items-center justify-center rounded-md px-3 py-1.5 text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 active:bg-green-200 transition-colors shadow-sm"
                    aria-label="Check web"
                    title="Search selected text on the web (useful for CFR references)"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span className="ml-1">Check web</span>
                  </button>
                  <div className="w-px h-5 bg-neutral-300 mx-1.5" />
                  <button
                    type="button"
                    onClick={() => {
                      const { from, to } = editor.state.selection;
                      const text = editor.state.doc
                        .textBetween(from, to, ' ')
                        .trim();
                      if (!text) return;
                      setRegenError(null);
                      setRegenPrompt('');
                      setShowRegen(true);
                    }}
                    className="flex items-center justify-center rounded-md px-3 py-1.5 text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 active:bg-amber-200 transition-colors shadow-sm"
                    aria-label="Regenerate"
                    title="Regenerate"
                  >
                    <RegenerateIcon />
                    <span className="ml-1">Regenerate</span>
                  </button>
                </BubbleMenu>
              )}

              {/* Partial Regenerate Modal */}
              {showRegen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-black/20"
                    onClick={() => (!regenLoading ? setShowRegen(false) : null)}
                  />
                  <div className="relative z-[61] w-full max-w-lg mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-4">
                    <h3 className="text-base font-semibold text-neutral-900 mb-2">
                      Regenerate selected text
                    </h3>
                    <p className="text-sm text-neutral-600 mb-3">
                      Provide an instruction for how to regenerate the selected
                      text.
                    </p>
                    <textarea
                      value={regenPrompt}
                      onChange={e => setRegenPrompt(e.target.value)}
                      className="w-full border border-neutral-300 rounded-md p-2 text-sm min-h-[96px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g. Make it shorter, more formal, fix grammar, add more details..."
                      disabled={regenLoading}
                    />
                    {regenError && (
                      <div className="mt-2 text-sm text-red-600">
                        {regenError}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setShowRegen(false)}
                        className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100"
                        disabled={regenLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!editor) return;
                          setRegenError(null);
                          const { from, to } = editor.state.selection;
                          const selected = editor.state.doc
                            .textBetween(from, to, ' ')
                            .trim();
                          if (!selected) {
                            setRegenError(
                              'Please select some text in the document.'
                            );
                            return;
                          }
                          if (!petitionId || !currentSection) {
                            setRegenError('Missing petition or section info.');
                            return;
                          }
                          setRegenLoading(true);

                          analytics.track(
                            ANALYTICS_EVENTS.PETITION_TEXT_REGENERATE_CLICKED,
                            {
                              petition_id: petitionId,
                              section_key: currentSection.key,
                              text_length: selected.length,
                              prompt_length: regenPrompt.length,
                            }
                          );

                          try {
                            // Provide some context around selection
                            const CONTEXT = 500;
                            const beforeFrom = Math.max(0, from - CONTEXT);
                            const afterTo = Math.min(
                              editor.state.doc.content.size,
                              to + CONTEXT
                            );
                            const beforeText = editor.state.doc
                              .textBetween(beforeFrom, from, ' ')
                              .trim();
                            const afterText = editor.state.doc
                              .textBetween(to, afterTo, ' ')
                              .trim();

                            const res =
                              await PetitionService.regenerateDraftSection(
                                petitionId,
                                currentSection.key,
                                regenPrompt || 'Improve this selection',
                                selected,
                                { beforeText, afterText },
                                'current-user' // TODO: Get from auth context
                              );

                            const next =
                              (res as any)?.data?.regeneratedContent ||
                              (res as any)?.regeneratedContent ||
                              '';
                            if (!next) {
                              throw new Error('Empty response');
                            }
                            editor.chain().focus().insertContent(next).run();

                            // Save the updated content
                            setTimeout(() => {
                              const storage = editor.storage as any;
                              const updatedContent =
                                storage.markdown?.getMarkdown?.() ||
                                editor.getText();
                              saveContent(updatedContent);
                            }, 100);

                            setShowRegen(false);
                          } catch (err: any) {
                            setRegenError(
                              err?.message || 'Failed to regenerate.'
                            );
                          } finally {
                            setRegenLoading(false);
                          }
                        }}
                        className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        disabled={regenLoading}
                      >
                        {regenLoading && (
                          <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                        )}
                        Regenerate
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Full Section Regenerate Modal */}
              {showFullRegen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-black/40"
                    onClick={() => setShowFullRegen(false)}
                  />
                  <div className="relative z-[61] w-full max-w-lg mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-4">
                    <h3 className="text-base font-semibold text-neutral-900 mb-2">
                      Regenerate Section: {currentSection?.label}
                    </h3>
                    <p className="text-sm text-neutral-600 mb-3">
                      Provide instructions for how to regenerate this section.
                      The current content will be replaced.
                    </p>
                    <textarea
                      className="w-full border border-neutral-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={4}
                      placeholder="e.g., Make it more formal, add more technical details, emphasize specific achievements, etc."
                      value={fullRegenPrompt}
                      onChange={e => setFullRegenPrompt(e.target.value)}
                      autoFocus
                    />
                    {fullRegenError && (
                      <p className="text-xs text-red-600 mt-1">
                        {fullRegenError}
                      </p>
                    )}
                    <div className="flex items-center justify-end gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowFullRegen(false);
                          setFullRegenPrompt('');
                          setFullRegenError(null);
                        }}
                        className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 hover:bg-neutral-50"
                        disabled={fullRegenLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!editor || !petitionId || !currentSection) return;
                          setFullRegenLoading(true);
                          setFullRegenError(null);

                          analytics.track(
                            ANALYTICS_EVENTS.PETITION_TEXT_REGENERATE_CLICKED,
                            {
                              petition_id: petitionId,
                              section_key: currentSection.key,
                              section_name: currentSection.label,
                              prompt_length: fullRegenPrompt.length,
                              is_full_section: true,
                            }
                          );

                          try {
                            // Get current content as markdown
                            const storage = editor.storage as any;
                            const fullContent =
                              storage.markdown?.getMarkdown?.() ||
                              editor.getText();

                            const res =
                              await PetitionService.regenerateDraftSection(
                                petitionId,
                                currentSection.key,
                                fullRegenPrompt ||
                                  `Regenerate the ${currentSection.label} section`,
                                fullContent,
                                {},
                                'current-user' // TODO: Get from auth context
                              );

                            const next =
                              (res as any)?.data?.regeneratedContent ||
                              (res as any)?.regeneratedContent ||
                              '';
                            if (!next) {
                              throw new Error('Empty response');
                            }

                            // Replace entire content
                            editor.commands.setContent(next);

                            // Save the updated content
                            setTimeout(() => {
                              const storageAfter = editor.storage as any;
                              const updatedContent =
                                storageAfter.markdown?.getMarkdown?.() ||
                                editor.getText();
                              saveContent(updatedContent);
                            }, 100);

                            setShowFullRegen(false);
                            setFullRegenPrompt('');
                          } catch (err: any) {
                            setFullRegenError(
                              err?.message || 'Failed to regenerate section.'
                            );
                          } finally {
                            setFullRegenLoading(false);
                          }
                        }}
                        className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        disabled={fullRegenLoading}
                      >
                        {fullRegenLoading && (
                          <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                        )}
                        Regenerate Section
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Copy button */}
              {editor && editor.getText() && (
                <button
                  type="button"
                  onClick={copyText}
                  className="fixed bottom-8 right-8 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-primary-700 transition-colors z-50 inline-flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <CheckIcon /> Copied!
                    </>
                  ) : (
                    <>
                      <CopyIcon /> Copy Section
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !approvalLoading && setShowApproveModal(false)}
          />
          <div className="relative z-[61] w-full max-w-md mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">
              Approve Section
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              Are you sure you want to approve{' '}
              <strong>{currentSection?.label}</strong>? This will mark the
              section as ready for final submission.
            </p>
            {approvalError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {approvalError}
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowApproveModal(false);
                  setApprovalError(null);
                }}
                className="px-4 py-2 text-sm rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors"
                disabled={approvalLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApproveSection}
                className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={approvalLoading}
              >
                {approvalLoading && (
                  <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                )}
                Approve Section
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !approvalLoading && setShowRejectModal(false)}
          />
          <div className="relative z-[61] w-full max-w-md mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">
              Reject Section
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              Please provide a reason for rejecting{' '}
              <strong>{currentSection?.label}</strong>. This will help guide
              revisions.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              className="w-full border border-neutral-300 rounded-md p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="e.g., This section needs more specific examples of achievements, or citations are missing for key claims..."
              disabled={approvalLoading}
              autoFocus
            />
            {approvalError && (
              <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {approvalError}
              </div>
            )}
            <div className="flex items-center justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setApprovalError(null);
                }}
                className="px-4 py-2 text-sm rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors"
                disabled={approvalLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectSection}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={approvalLoading || !rejectReason.trim()}
              >
                {approvalLoading && (
                  <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                )}
                Reject Section
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingSectionKey && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDeletingSectionKey(null)}
          />
          <div className="relative z-[61] w-full max-w-md mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">
              Delete Section
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              Are you sure you want to delete the section{' '}
              <strong>
                {
                  availableSections.find(s => s.key === deletingSectionKey)
                    ?.label
                }
              </strong>
              ? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeletingSectionKey(null)}
                className="px-4 py-2 text-sm rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors inline-flex items-center gap-2"
              >
                <DeleteIcon className="h-4 w-4" />
                Delete Section
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetitionEditor;
